/**
 * SearchBarAutoComplete
 * 
 * @Author: Focci
 * @Date: 2023-08-25 14:49:39
 * @Last Modified by: Focci
 * @Last Modified time: 2023-08-25 14:49:39
 */

'use client'

import PropTypes from 'prop-types'
import useTracking from '@hook/useTracking'
import useSearchRecording from '@hook/useSearchRecording'
// import useStorage from '@hook/useStorage'
import useProgressRouter from '@hook/useProgressRouter'
import useSurroundingCheckbox from '@page/common/filter/useSurroundingCheckbox'
import ResposiveAutocomplete from '@comp/page/common/filter/combine/resposive'
import SearchSuggestion from '@page/common/filter/combine/suggestion'
import SearchAutocomplete, { handleSearchItemClick } from '@page/common/filter/combine'
import useApp from '@hook/useApp'
import dynamic from 'next/dynamic'
import { houseSearchType } from '@lib/config'
import { FilterSkeletonCircular } from '@comp/page/common/filter/listing/FilterSkeleton'
import { getListingModeParam } from '@lib/listing-mode'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { filter, isObject } from 'lodash-es'
import { getSearchDefaultValue } from '@comp/page/common/filter/combine/lib'
import { getGroupedData } from '@comp/page/common/filter/combine/filter-modal/lib'
import { useLocale, useTranslations } from 'next-intl'
import { urls, args } from '@lib/parse'
import SearchButton from './SearchButton'

const FilterModal = dynamic(() => import('@page/common/filter/combine/filter-modal/index'), {
  ssr: false,
  loading: FilterSkeletonCircular
})

export default function SearchBarAutoComplete({ 
  type,
  ResposiveTrigger,
  autofocus = false
}) {
  const ts = useTranslations('search')
  const tt = useTranslations('track')
  const locale = useLocale()

  const router = useProgressRouter()
  const { surrounding, handleCheckboxChange, SurroundingCheckbox } = useSurroundingCheckbox()
  const { pushTrack, pushTrackSearch } = useTracking()
  // const { getItem, setItem } = useStorage('HG_IS_FIRST_SEARCH_IN_HOME', true)
  const { records, addSearchRecord } = useSearchRecording()
  const { isMobile } = useApp()

  const [value, setValue] = useState([])
  const [highlight, setHighlight] = useState()
  const [initial, setInitial] = useState()
  const [isMapListing, setIsMapListing] = useState()
  // const [surrounding, setSurrounding] = useState(true) // 是否包含周边地区

  /**
   * 生成列表url, 并跳转
   */
  const toListing = useCallback((typ, searchParam, toMapListing) => {
    const url = urls.listing(typ, searchParam, locale, toMapListing)
    const apiParam = args.listingApi(typ, searchParam)
    // 添加访问记录
    addSearchRecord({ type: typ, searchParam, apiParam })
    
    // house_search事件
    pushTrackSearch({ ...searchParam, type: typ }, apiParam, toMapListing)
    router.routerPush(url)
  }, [addSearchRecord, pushTrackSearch, locale, router])
  
  /**
   * 确认搜索
   */
  const handleConfirm = useCallback((v) => {
    const val = v || value
    const { param: searchParam, isMapListing: iml } = getListingModeParam({ 
      ...getGroupedData(val),
      ...(surrounding ? { surrounding } : null),
      page: 1 
    }, isMobile)
    
    // 如果是第一次搜索, 弹出filter面板
    // if(isFirstSearch || isMobile) {
    // setItem(true)
    setIsMapListing(iml)
    setInitial({
      type, 
      defaultSearchValue: val, 
      apiParam: args.listingApi(type, searchParam), 
      searchParam,
    })
    // } else {
    //   toListing(type, searchParam, iml)
    // }
  }, [isMobile, surrounding, type, value])

  /**
   * 搜索change
   */
  const handleChange = useCallback((_event, data, reason, opt) => {
    const p = { data, reason, type, setter: setValue, pushTrack, pushTrackSearch, locale }
    const r = handleSearchItemClick(p, (v) => opt?.isZoneSchoolPicker && handleConfirm(v))
    
    r?.url && router.routerPush(r.url)
    // 添加搜索记录
    r?.targetData && addSearchRecord(r.targetData)
  }, [router, type, pushTrack, pushTrackSearch, addSearchRecord, handleConfirm, locale])

  /**
   * filter modal confirm
   */
  const handleFilterModealConfirm = useCallback(
    ({ currentType, filterObject }) => toListing(currentType, filterObject, isMapListing),
    [isMapListing, toListing]
  )

  /**
   * 添加回车支持
   */
  const handleKeyDown = useCallback((e) => {
    if(e.keyCode === 13 && !highlight && value.length > 0) {
      handleConfirm()
    }
  }, [handleConfirm, highlight, value.length])
  
  // 向搜索框内填充默认值
  useEffect(() => {
    const record = filter(records, (f) => f.type === type)[0]
    const tmp = isObject(record) ? getSearchDefaultValue(record.data) : []

    setValue(tmp)
  }, [records, type])

  const autocompleteProps = useMemo(() => ({
    autofocus,
    noOptionsText: (
      <SearchSuggestion
        type={type}
        inputValue={value}
        inputValueSetter={setValue} 
      />
    )
  }), [type, value, autofocus])
  
  return (
    <>
      <div className="hidden md:block">
        <SearchAutocomplete
          value={value}
          type={type}
          onChange={handleChange}
          autocompleteProps={{
            ...autocompleteProps,
            size: 'large',
            onKeyDown: handleKeyDown,
            onHighlightChange: (e) => setHighlight(e),
            onClose: () => setHighlight(),
          }}
        >
          <SearchButton
            disabled={value.length === 0} 
            onClick={() => handleConfirm()} 
          />
        </SearchAutocomplete>
        <div className="h-4 mt-2 overflow-hidden leading-none">
          <SurroundingCheckbox
            checked={surrounding}
            onChange={handleCheckboxChange}
          />
        </div>
      </div>
      <div className="block md:hidden">
        <ResposiveAutocomplete
          value={value}
          type={type}
          showSurrounding={true}
          // placeholder={ts('searchInputPlaceholder')}
          placeholder={type === houseSearchType.estimate ? tt('searchByTypeAddr') : ts('searchInputPlaceholder')}
          onChange={handleChange}
          onSearchButtonClick={() => handleConfirm()}
          autocompleteProps={autocompleteProps}
          showSubburbButtonPicker={false}
          autocompleteTriggerProps={{
            className: 'w-full min-h-12'
          }}
          Trigger={ResposiveTrigger}
        />
      </div>
      {initial && (
        <FilterModal
          open={true}
          initial={initial}
          onClose={() => setInitial()}
          onConfirm={handleFilterModealConfirm}
        />
      )}
    </>
  )
}

SearchBarAutoComplete.propTypes = {
  type: PropTypes.string,
}
